import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Send, Bot, User as UserIcon, AlertTriangle, Coins, Loader2 } from "lucide-react";
import { AgentPipeline, AgentsRun } from "@/components/workspace/AgentPipeline";

const SUGGESTIONS = ["Cambia el color del menú", "Agrega un reporte de ventas", "Quiero que mis clientes puedan registrarse", "Corrige el formulario de productos"];

function Message({ m }) {
  const user = m.role === "user";
  return (
    <div className={`flex gap-3 ${user ? "flex-row-reverse" : ""}`} data-testid={`chat-message-${m.role}`}>
      <span className={`shrink-0 w-7 h-7 rounded-lg grid place-items-center ${user ? "bg-blue-500/20 text-blue-300" : "bg-emerald-500/15 text-emerald-300"}`}>
        {user ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </span>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${user ? "bg-blue-500/15 text-slate-100" : "bg-[#161C2E] text-slate-200 border border-[#222B40]"}`}>
        {m.content}
        {m.credits != null && <p className="mt-1 font-mono text-[10px] text-slate-500">{m.credits} créditos</p>}
        {m.version && <p className="mt-2 font-mono text-[11px] text-cyan-400" data-testid="chat-message-version">→ versión v{m.version}{m.quality ? (m.quality.passed ? " · verificación superada" : " · verificación con errores") : ""}</p>}
        {!user && <AgentsRun list={m.agents_run} />}
      </div>
    </div>
  );
}

export function ChatPanel({ messages, live, input, setInput, onSend, running, credits }) {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, live]);
  const noCredits = credits < 1;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto scroll-thin p-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && !live && (
          <div className="text-sm text-slate-400 p-4 rounded-xl border border-dashed border-[#222B40]" data-testid="chat-empty">
            Describe la aplicación que quieres crear. El equipo de agentes analizará tu idea, diseñará la solución, escribirá el código y lo verificará.
          </div>
        )}
        {messages.map((m) => <Message key={m.id} m={m} />)}
        {live && (
          <div className="space-y-3">
            <AgentPipeline state={live.agents} chars={live.chars} />
            {live.text && <div className="rounded-2xl px-4 py-3 text-sm bg-[#161C2E] border border-[#222B40] text-slate-200 whitespace-pre-wrap" data-testid="chat-live-text">{live.text}</div>}
          </div>
        )}
        <div ref={end} />
      </div>
      <div className="p-3 border-t border-white/5 space-y-2">
        {noCredits && (
          <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="chat-no-credits">
            <AlertTriangle className="w-4 h-4 shrink-0" /> No tienes créditos. Cada operación muestra su costo estimado antes de enviarse. <Link to="/app/creditos" className="underline" data-testid="chat-buy-credits-link">Comprar créditos</Link>
          </div>
        )}
        {messages.some((m) => m.version) && !running && (
          <div className="flex gap-1.5 overflow-x-auto scroll-thin pb-1">
            {SUGGESTIONS.map((s, i) => <button key={s} onClick={() => setInput(s)} data-testid={`chat-suggestion-${i}`} className="shrink-0 text-[11px] px-2.5 py-1 rounded-full border border-[#222B40] text-slate-400 hover:text-cyan-200 hover:border-cyan-500/40">{s}</button>)}
          </div>
        )}
        <div className="relative">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={3} disabled={running} data-testid="chat-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            placeholder="Describe tu app o pide un cambio…" className="w-full resize-none rounded-xl bg-[#111622] border border-[#222B40] focus:border-cyan-500/50 outline-none px-3 py-2.5 pr-12 text-sm text-slate-100 placeholder:text-slate-500" />
          <button onClick={onSend} disabled={running || !input.trim() || noCredits} data-testid="chat-send-btn"
            className="absolute right-2 bottom-3 w-8 h-8 grid place-items-center rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white transition-colors">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 flex items-center gap-1"><Coins className="w-3 h-3" />Verás el costo estimado antes de enviar · saldo <span data-testid="chat-credits-balance">{credits}</span></p>
      </div>
    </div>
  );
}
